import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, FormControl, MenuItem, Select, Stack, Typography } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { useState } from 'react';
import Chart from 'react-apexcharts';

import { colors } from 'src/global/styles/colors';
import { useProjectDashboardContext } from 'src/hooks/useProjectDashboardContext';
import { useGetProjectOtcRevenuesQuery } from 'src/store/services/project-dashboard.service';

import { DEFAULT_TIME_PERIOD, TIME_PERIOD_OPTIONS } from '../constants/timePeriods';

const OTCRevenueChart = () => {
  const [selectedPeriod, setSelectedPeriod] = useState(DEFAULT_TIME_PERIOD);
  const { projectId, address, network } = useProjectDashboardContext();

  const {
    data: chartData,
    isLoading,
    error,
  } = useGetProjectOtcRevenuesQuery(
    {
      projectId,
      period: selectedPeriod as any,
      size: 50,
      address,
      network,
    },
    { skip: !projectId || !address || !network },
  );

  const handlePeriodChange = (newPeriod: string) => {
    setSelectedPeriod(newPeriod);
  };

  const chartOptions: ApexOptions = {
    chart: {
      type: 'area',
      height: 296,
      toolbar: {
        show: false,
      },
      background: 'transparent',
      fontFamily: 'Gotham, sans-serif',
    },
    theme: {
      mode: 'dark',
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
      width: 2,
      colors: ['#4CC3C1'],
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.5,
        opacityTo: 0,
        stops: [0, 100],
        colorStops: [
          {
            offset: 0,
            color: '#4CC3C1',
            opacity: 0.5,
          },
          {
            offset: 100,
            color: '#4CC3C1',
            opacity: 0,
          },
        ],
      },
    },
    grid: {
      borderColor: 'rgba(255, 255, 255, 0.03)',
      strokeDashArray: 6,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    xaxis: {
      categories: chartData?.result?.map(item => item.s) || [],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: colors.neutral90,
          fontSize: '12px',
          fontFamily: 'Gotham, sans-serif',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: colors.neutral90,
          fontSize: '12px',
          fontFamily: 'Gotham, sans-serif',
        },
        formatter: value => `$${value}`,
      },
      tickAmount: 6,
    },
    tooltip: {
      theme: 'dark',
      style: {
        fontSize: '12px',
        fontFamily: 'Gotham, sans-serif',
      },
      y: {
        formatter: value => `$${value}`,
      },
    },
    legend: {
      show: false,
    },
  };

  return (
    <Box
      sx={{
        background: colors.neutral03,
        backdropFilter: 'blur(100px)',
        borderRadius: '8px',
        padding: '24px',
        height: '392px',
      }}>
      <Stack spacing={3} height='100%'>
        {/* Header */}
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography
            sx={{
              color: colors.white,
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '24px',
            }}>
            LFG's Revenue via OTC
          </Typography>

          <FormControl size='small'>
            <Select
              value={selectedPeriod}
              onChange={e => handlePeriodChange(e.target.value)}
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                backgroundColor: colors.neutral03,
                color: colors.white,
                fontSize: '12px',
                borderRadius: '4px',
                minWidth: '120px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
                '& .MuiSvgIcon-root': {
                  color: colors.white,
                },
              }}>
              {TIME_PERIOD_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Chart */}
        <Box sx={{ flex: 1, width: '100%' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography sx={{ color: colors.neutral90 }}>Loading chart data...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography sx={{ color: colors.red200, textAlign: 'center' }}>Failed to load chart data</Typography>
            </Box>
          ) : (
            <Chart
              options={chartOptions}
              series={[
                {
                  name: "LFG's Revenue via OTC",
                  data: chartData?.result?.map(item => item.y) || [],
                },
              ]}
              type='area'
              height='100%'
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default OTCRevenueChart;
